/* eslint-disable @typescript-eslint/no-explicit-any */
import { DashOutlined } from '@ant-design/icons'
import { SorterResult } from 'antd/es/table/interface'
import { DefineTypeEventEnum } from 'app/api/event/constant'
import { IEvent, TypeEventEnum } from 'app/api/event/model'
import R from 'app/assets/R'
import { ENV_CONFIG } from 'app/common/config'
import { PermissionEnum } from 'app/common/enum'
import { ISort } from 'app/common/interface'
import { BaseButton } from 'app/components/common/BaseButton'
import { BasePopover } from 'app/components/common/BasePopover'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import {
  EVENT_MANAGEMENT_DETAILS_PATH
} from 'app/components/router/route-path'
import { RenderValueTable } from 'app/components/tables/BaseTableManagement/hook'
import { convertColumnTable } from 'app/components/tables/BaseTableReport/hook'
import { FORMAT_DD_MM_YYYY_HH_MM } from 'app/constant'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import { useDebounce, usePagination } from 'app/hook'
import { useGetEventList } from 'app/react-query/hook/event'
import { IResponseQueryList } from 'app/react-query/model/common'
import { handleDownloadExcel } from 'app/service/general/service'
import { convertedVariables } from 'app/styles/themes/themeVariables'
import { isEqual } from 'lodash'
import { DateUtil, FONT_WEIGHT, Pagination } from 'parkway-web-common'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { DeleteModalLayout } from './layouts'
import { StatusEventWrapper } from './styles'
import {
  DefineEventStatus,
  EventStatusEnum,
  IEventTableData,
  IFilterEventList,
} from './type'

export const useHook = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [filter, setFilter] = useState<IFilterEventList>({})

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sorts, setSort] = useState<ISort[]>([])

  const filterDebounce = useDebounce(filter, 500)

  const { data: dataEvent, isLoading } = useGetEventList({
    page,
    limit: pageSize,
    search: filterDebounce?.search,
    status: filterDebounce?.status,
    type: filterDebounce?.type,
    sorts,
    startDate: filterDebounce?.startDateDayJs?.toISOString(),
    endDate: filterDebounce?.endDateDayJs?.toISOString(),
  })

  const { flattenDataList } = usePagination()

  const eventData = useMemo(() => {
    const res: IResponseQueryList<IEvent[]> = flattenDataList(dataEvent)
    return res
  }, [dataEvent])

  const { user, verifyPermission } = useVerifyPermission()

  const isHavePermissionDeleteEvent = useMemo(() => {
    return verifyPermission(PermissionEnum.DeleteEvent)
  }, [user])

  const data: IEventTableData[] = useMemo(() => {
    return eventData?.data?.map((item, index) => {
      return {
        ...item,
        key: index + 1,
      }
    })
  }, [eventData])

  const onNavigateDetail = (event: IEvent) => {
    navigate(EVENT_MANAGEMENT_DETAILS_PATH, {
      state: {
        event,
      },
    })
  }

  const columns = useMemo(() => {
    return [
      convertColumnTable<IEventTableData>({
        key: 'name_event',
        title: t(R.strings.event_name),
        classNameWidthColumnOverwrite: 'very-big-column',
        fixed: 'left',
        sorter: true,
        render: (_, record) => {
          return (
            <RenderValueTable
              value={record?.name_event}
              type="Link"
              onClick={() => onNavigateDetail(record)}
            />
          )
        },
      }),
      convertColumnTable<IEventTableData>({
        key: 'description',
        title: 'Thông tin sự kiện',
        classNameWidthColumnOverwrite: 'big-column',
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={<RenderText text={record?.description ?? ''} />}
            />
          )
        },
      }),
      convertColumnTable<IEventTableData>({
        key: 'time_register_start',
        title: t(R.strings.start_date_register),
        sorter: true,
        render: (_, record) => {
          if (isEqual(record?.type, TypeEventEnum.NEWS)) {
            return '-'
          }
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={
                record?.time_register_start
                  ? DateUtil?.formatDate(
                      record?.time_register_start ?? '',
                      FORMAT_DD_MM_YYYY_HH_MM,
                    )
                  : '-'
              }
            />
          )
        },
      }),
      convertColumnTable<IEventTableData>({
        key: 'time_register_end',
        title: t(R.strings.end_date_register),
        sorter: true,
        render: (_, record) => {
          if (isEqual(record?.type, TypeEventEnum.NEWS)) {
            return '-'
          }
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={
                record?.time_register_end
                  ? DateUtil?.formatDate(
                      record?.time_register_end ?? '',
                      FORMAT_DD_MM_YYYY_HH_MM,
                    )
                  : '-'
              }
            />
          )
        },
      }),
      convertColumnTable<IEventTableData>({
        key: 'time_start',
        title: t(R.strings.start_date_event),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={
                record?.time_start
                  ? DateUtil?.formatDate(
                      record?.time_start ?? '',
                      FORMAT_DD_MM_YYYY_HH_MM,
                    )
                  : '-'
              }
            />
          )
        },
      }),

      convertColumnTable<IEventTableData>({
        key: 'time_end',
        title: t(R.strings.end_date_event),
        sorter: true,
        render: (_, record) => {
          return (
            <BaseText
              fontWeight="medium"
              opacity="0.7"
              children={
                record?.time_end
                  ? DateUtil?.formatDate(
                      record?.time_end ?? '',
                      FORMAT_DD_MM_YYYY_HH_MM,
                    )
                  : '-'
              }
            />
          )
        },
      }),

      convertColumnTable({
        key: 'type',
        title: 'Loại sự kiện',
        sorter: true,
        render: (type: TypeEventEnum) => {
          return (
            <BaseText
              children={DefineTypeEventEnum[type]?.text ?? '-'}
              fontWeight="semibold"
              colorText="statesGreenColor"
            />
          )
        },
      }),

      convertColumnTable<IEventTableData>({
        key: 'status',
        title: t(R.strings.status),
        fixed: 'right',
        render: (_, record) => {
          if (!record?.status) return null

          return (
            <StatusEventWrapper
              $status={(() => {
                return record?.status
              })()}
            >
              <BaseText
                children={(() => {
                  return t(DefineEventStatus[record?.status]?.keyI18n)
                })()}
                colorText={(() => {
                  switch (record?.status) {
                    case EventStatusEnum.NOT_OPEN:
                      return 'otherBlueColor'
                    case EventStatusEnum.REGISTRATION:
                      return 'otherBlueColor'
                    case EventStatusEnum.REGISTRATION_COMPLETED:
                      return 'statesOrangeColor'
                    case EventStatusEnum.IN_PROGRESS:
                      return 'statesGreenColor'
                    case EventStatusEnum.FINISHED:
                      return 'statesRedColor'
                  }
                })()}
                fontWeight="medium"
              />
            </StatusEventWrapper>
          )
        },
      }),

      convertColumnTable<IEventTableData>({
        key: 'key',
        title: '',
        fixed: 'right',
        classNameWidthColumnOverwrite: 'number-column',
        render: (_, record) => {
          return (
            <BasePopover
              trigger="click"
              content={
                <BaseSpace>
                  <BaseButton
                    type="text"
                    onClick={() => onNavigateDetail(record)}
                  >
                    <BaseText
                      children={'Xem chi tiết'}
                      fontWeight="medium"
                      fontSize="xs"
                    />
                  </BaseButton>

                  {isEqual(record?.status, EventStatusEnum.NOT_OPEN) ||
                  isEqual(record?.status, EventStatusEnum.REGISTRATION) ||
                  isEqual(
                    record?.status,
                    EventStatusEnum.REGISTRATION_COMPLETED,
                  ) ? (
                    isHavePermissionDeleteEvent ? (
                      <DeleteModalLayout
                        id={record?.id}
                        name={record?.name_event}
                      />
                    ) : null
                  ) : null}
                </BaseSpace>
              }
            >
              <BaseButton
                icon={<DashOutlined rev={undefined} rotate={90} />}
                type="text"
              />
            </BasePopover>
          )
        },
      }),
    ]
  }, [t])

  const onChangeFilter = (newFilter: IFilterEventList) => {
    setFilter(newFilter)
    setPage(1)
  }

  const handleChangeTable = (
    pagination: Pagination,
    _,
    sorter: SorterResult<any> | SorterResult<any>[],
  ) => {
    setPage(pagination?.current ?? 1)
    setPageSize(pagination?.pageSize ?? 10)

    let sortParams: { field: string; order: 'ASC' | 'DESC' }[] = []

    if (Array.isArray(sorter)) {
      // Nếu sorter là mảng (multi-sort)
      sortParams = sorter
        .filter(s => s.order) // Lọc ra những cột có thứ tự sắp xếp
        .map(s => ({
          field: s.field as string,
          order: s.order === 'ascend' ? 'ASC' : 'DESC',
        }))
    } else if (sorter.order) {
      // Nếu chỉ có một cột được sort
      sortParams = [
        {
          field: sorter.field as string,
          order: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        },
      ]
    }

    setSort(sortParams)
  }

  const downloadExcel = async (params: {
    search?: string
    type?: string[]
    status?: string[]
    startDate?: string
    endDate?: string
  }): Promise<void> => {
    await handleDownloadExcel({
      params: {
        ...params,
      },
      urlMethod: `${ENV_CONFIG.API_ENDPOINT}admin/event/download`,
      fileName: 'Sự kiện',
    })
  }

  const handleDownloadEventExcel = async () => {
    await downloadExcel({
      search: filterDebounce?.search,
      status: filterDebounce?.status,
      type: filterDebounce?.type,
      startDate: filterDebounce?.startDateDayJs?.toISOString(),
      endDate: filterDebounce?.endDateDayJs?.toISOString(),
    })
  }

  return {
    isLoading,
    columns,
    data,
    onChangeFilter,
    filter,
    page,
    pageSize,
    total: eventData?.total,
    handleChangeTable,
    handleDownloadEventExcel,
  }
}

export const RenderText: React.FC<{ text: string }> = ({ text }) => {
  const urlRegex = /(https?:\/\/[^\s<>"']+)/g

  return (
    <span>
      {text.split(urlRegex).map((part, index) =>
        urlRegex.test(part) ? (
          <a
            style={{
              color: convertedVariables.collapseBackgroundColor,
              fontWeight: FONT_WEIGHT.regular,
            }}
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
          >
            {part}
          </a>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </span>
  )
}
